import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import type { RootState } from "../../../common/state/store";

export interface IConnectedDevice {
  serial: string;
  productName: string;
  manufacturerName: string;
  status: "connecting" | "authenticating" | "ready" | "error";
  errorMessage?: string;
}

export interface IInstallJob {
  buildId?: number;
  fileName: string;
  packageName?: string;
  /** When set, this install was performed via the desktop agent (not ADB) */
  agentAppId?: string;
  /** Job ID assigned by the desktop agent (for cancellation) */
  agentJobId?: string;
  status: "downloading" | "extracting" | "pushing" | "installing" | "success" | "error";
  progress: number;
  totalSizeMB?: number;
  errorMessage?: string;
}

export interface IInstalledPackage {
  packageName: string;
  versionCode: number | null;
}

export interface IMediaPushJob {
  projectGroupId: number;
  packageName: string;
  status: "fetching_manifest" | "pushing" | "success" | "error" | "cancelled";
  totalFiles: number;
  pushedFiles: number;
  failedFiles: number;
  skippedFiles: number;
  currentFileName: string;
  currentFileProgress: number;
  errorMessage?: string;
}

/** Lightweight build info registered by enduser pages for cross-component visibility */
export interface IEnduserBuildInfo {
  packageName: string;
  targetPlatform: string;
  version: number;
  projectGroupName: string;
  s3Url: string;
  buildId: number;
}

interface IDeviceInstallerState {
  device: IConnectedDevice | null;
  installJob: IInstallJob | null;
  error: string | null;
  /** Packages currently installed on the connected device, keyed by packageName */
  installedPackages: Record<string, IInstalledPackage>;
  packageScanStatus: "idle" | "scanning" | "done" | "error";
  /** Builds registered by enduser pages, keyed by packageName */
  enduserBuilds: Record<string, IEnduserBuildInfo>;
  /** Current media pre-push job */
  mediaPushJob: IMediaPushJob | null;
}

const initialState: IDeviceInstallerState = {
  device: null,
  installJob: null,
  error: null,
  installedPackages: {},
  packageScanStatus: "idle",
  enduserBuilds: {},
  mediaPushJob: null,
};

export const deviceInstallerSlice = createSlice({
  name: "deviceInstaller",
  initialState,
  reducers: {
    setDevice: (state, action: PayloadAction<IConnectedDevice>) => {
      state.device = action.payload;
      state.error = null;
    },
    updateDeviceStatus: (
      state,
      action: PayloadAction<{ status: IConnectedDevice["status"]; errorMessage?: string }>,
    ) => {
      if (state.device) {
        state.device.status = action.payload.status;
        state.device.errorMessage = action.payload.errorMessage;
      }
    },
    clearDevice: (state) => {
      state.device = null;
      state.installedPackages = {};
      state.packageScanStatus = "idle";
    },
    setInstallJob: (state, action: PayloadAction<IInstallJob>) => {
      state.installJob = action.payload;
    },
    updateInstallProgress: (state, action: PayloadAction<number>) => {
      if (state.installJob) {
        state.installJob.progress = action.payload;
      }
    },
    updateInstallStatus: (
      state,
      action: PayloadAction<{ status: IInstallJob["status"]; errorMessage?: string }>,
    ) => {
      if (state.installJob) {
        state.installJob.status = action.payload.status;
        state.installJob.errorMessage = action.payload.errorMessage;
      }
    },
    updateInstallTotalSize: (state, action: PayloadAction<number>) => {
      if (state.installJob) {
        state.installJob.totalSizeMB = action.payload;
      }
    },
    updateAgentJobId: (state, action: PayloadAction<string>) => {
      if (state.installJob) {
        state.installJob.agentJobId = action.payload;
      }
    },
    clearInstallJob: (state) => {
      state.installJob = null;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setInstalledPackages: (state, action: PayloadAction<Record<string, IInstalledPackage>>) => {
      state.installedPackages = action.payload;
    },
    removeInstalledPackage: (state, action: PayloadAction<string>) => {
      delete state.installedPackages[action.payload];
    },
    setPackageScanStatus: (
      state,
      action: PayloadAction<IDeviceInstallerState["packageScanStatus"]>,
    ) => {
      state.packageScanStatus = action.payload;
    },
    setEnduserBuilds: (state, action: PayloadAction<Record<string, IEnduserBuildInfo>>) => {
      state.enduserBuilds = { ...state.enduserBuilds, ...action.payload };
    },
    setMediaPushJob: (state, action: PayloadAction<IMediaPushJob>) => {
      state.mediaPushJob = action.payload;
    },
    updateMediaPushProgress: (
      state,
      action: PayloadAction<{
        pushedFiles: number;
        currentFileName: string;
        currentFileProgress: number;
      }>,
    ) => {
      if (state.mediaPushJob) {
        state.mediaPushJob.pushedFiles = action.payload.pushedFiles;
        state.mediaPushJob.currentFileName = action.payload.currentFileName;
        state.mediaPushJob.currentFileProgress = action.payload.currentFileProgress;
      }
    },
    updateMediaPushStatus: (
      state,
      action: PayloadAction<{
        status: IMediaPushJob["status"];
        errorMessage?: string;
        failedFiles?: number;
        skippedFiles?: number;
      }>,
    ) => {
      if (state.mediaPushJob) {
        state.mediaPushJob.status = action.payload.status;
        state.mediaPushJob.errorMessage = action.payload.errorMessage;
        if (action.payload.failedFiles !== undefined) {
          state.mediaPushJob.failedFiles = action.payload.failedFiles;
        }
        if (action.payload.skippedFiles !== undefined) {
          state.mediaPushJob.skippedFiles = action.payload.skippedFiles;
        }
      }
    },
    clearMediaPushJob: (state) => {
      state.mediaPushJob = null;
    },
    clearDeviceInstallerState: () => initialState,
  },
});

export const {
  setDevice,
  updateDeviceStatus,
  clearDevice,
  setInstallJob,
  updateInstallProgress,
  updateInstallStatus,
  updateInstallTotalSize,
  updateAgentJobId,
  clearInstallJob,
  setError,
  setInstalledPackages,
  removeInstalledPackage,
  setPackageScanStatus,
  setEnduserBuilds,
  setMediaPushJob,
  updateMediaPushProgress,
  updateMediaPushStatus,
  clearMediaPushJob,
  clearDeviceInstallerState,
} = deviceInstallerSlice.actions;

export const deviceInstallerReducer = deviceInstallerSlice.reducer;

export const getConnectedDevice = (state: RootState) => state.deviceInstaller.device;
export const getInstallJob = (state: RootState) => state.deviceInstaller.installJob;
export const getDeviceInstallerError = (state: RootState) => state.deviceInstaller.error;
export const getInstalledPackages = (state: RootState) => state.deviceInstaller.installedPackages;
export const getPackageScanStatus = (state: RootState) => state.deviceInstaller.packageScanStatus;
export const getEnduserBuilds = (state: RootState) => state.deviceInstaller.enduserBuilds;
export const getMediaPushJob = (state: RootState) => state.deviceInstaller.mediaPushJob;
